import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { PillarDetailPane } from './pillars/PillarDetailPane';
import { PillarCard } from './pillars/PillarCard';
import { useCultureHubQueryParams } from '../../hooks/useCultureHubQueryParams';
import { useGetCultureHubPillarsQuery } from '../../api/cultureHubApi';
import { useApiPortalV1AssessmentsResultsListQuery } from '@/store/generated/portal/assessments/results';
import { getTeamSpecificThemeResults } from '../../utils/themeHelpers';

import LoadingSpinner from 'common/components/LoadingSpinner';
import { isCurrentMemberTeamLeader, getMemberId } from '@/common/selectors/auth';
import {
  getFilteredPillars,
  getPillars,
  setPillars,
} from '@/areas/culture-hub/slices/cultureHubInterventionsSlice';
import { Title, Caption, PageContent, PageSection } from '@/common/styles/page';

import { AggregateType } from '@/common/constants/AggregateType';
interface PillarsTabProps {}

const PillarsLayout = styled.div`
  display: grid;
  gap: 3.5rem;
  grid-template-columns: 1fr;

  @media (min-width: 1024px) {
    grid-template-columns: 320px minmax(0, 1fr);
    align-items: start;
  }
`;

const PillarColumn = styled.div`
  display: grid;
  gap: 1.0rem;
`;

const PillarDetailWrapper = styled.div`
  width: 100%;
  max-width: 900px;

  @media (min-width: 1024px) {
    padding-left: 1.5rem;
    border-left: 1px solid rgba(229, 231, 235, 1);
  }
`;


export const PillarsTab: React.FC<PillarsTabProps> = () => {
  const dispatch = useDispatch();
  const pillars = useSelector(getPillars);
  const filteredPillars = useSelector(getFilteredPillars);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const isTeamLeader = useSelector(isCurrentMemberTeamLeader);
  const currentUserId = useSelector(getMemberId);

  const { assessmentPeriodId, teamId, organizationId, shouldFetchData } = useCultureHubQueryParams();
  const queryTeamId = teamId;

  const { data: pillarsData, isLoading: pillarsLoading, isFetching: pillarsFetching } = useGetCultureHubPillarsQuery(
    { assessmentPeriodId: assessmentPeriodId!, teamId: queryTeamId },
    { skip: !assessmentPeriodId || !shouldFetchData || !queryTeamId, refetchOnFocus: false }
  );

  const { data: ai_themes_data, isLoading: aiThemesLoading, isFetching: aiThemesFetching } = useApiPortalV1AssessmentsResultsListQuery(
    {
      aggregateType: AggregateType.THEMES,
      assessmentPeriodId: assessmentPeriodId!,
      employeeId: currentUserId ?? undefined,
    },
    { skip: !assessmentPeriodId || !organizationId || !selectedKey, refetchOnFocus: false }
  );

  const { data: ai_sentiment_data, isLoading: aiSentimentLoading, isFetching: aiSentimentFetching } = useApiPortalV1AssessmentsResultsListQuery(
    {
      aggregateType: AggregateType.SENTIMENT_SCORES,
      assessmentPeriodId: assessmentPeriodId!,
      employeeId: currentUserId ?? undefined,
    },
    { skip: !assessmentPeriodId || !organizationId || !selectedKey, refetchOnFocus: false }
  );

  useEffect(() => {
    if (pillarsData?.pillars) {
      const aiResults = getTeamSpecificThemeResults(ai_themes_data, queryTeamId);

      const sentimentRaw: any = ai_sentiment_data;
      let sentimentResults: any[] = [];

      if (Array.isArray(sentimentRaw) && sentimentRaw.length > 0 && Array.isArray(sentimentRaw[0]?.result?.team_pillar_sentiments)) {
        sentimentResults = sentimentRaw[0].result.team_pillar_sentiments as any[];
      }

      const nextPillars: Record<string, any> = { ...pillarsData.pillars };
      const normalize = (s: string) => (s || '').toString().trim();

      Object.keys(nextPillars).forEach((k) => {
        const pillar = nextPillars[k];
        const match = aiResults.find((r: any) => normalize(r.pillar_name) === normalize(pillar.name) || normalize(r.pillar_name) === normalize(pillar.key));
        if (match && Array.isArray(match.themes)) {
          const themes = match.themes.slice(0, 5).map((t: any) => ({
            key: (t.theme_title || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-'),
            label: t.theme_title,
            count: t.feedback_count,
            description: t.theme_description,
            representative_quotes: Array.isArray(t.representative_quotes) ? t.representative_quotes : [],
            sentiment: t.strength,
            signal_strength: typeof t.signal_strength === 'number' ? t.signal_strength : undefined,
          }));
          nextPillars[k] = { ...pillar, themes };
        }

        const sentimentMatch = sentimentResults.find((r: any) => {
          if (!queryTeamId || r.team_id == null) return false;
          if (Number(r.team_id) !== Number(queryTeamId)) return false;
          return normalize(r.pillar_name) === normalize(pillar.name) || normalize(r.pillar_name) === normalize(pillar.key);
        });

        if (sentimentMatch && typeof sentimentMatch.sentiment_score === 'number') {
          const score = Math.max(0, Math.min(10, Number(sentimentMatch.sentiment_score)));
          nextPillars[k] = { ...nextPillars[k], sentiment_score: score };
        }
      });

      dispatch(setPillars(nextPillars));
    }
  }, [dispatch, pillarsData?.pillars, ai_themes_data, ai_sentiment_data, queryTeamId]);

  useEffect(() => {
    if (!selectedKey && filteredPillars.length > 0) {
      setSelectedKey(filteredPillars[0].key);
    }
    if (selectedKey && filteredPillars.length > 0 && !filteredPillars.some((p) => p.key === selectedKey)) {
      setSelectedKey(filteredPillars[0].key);
    }
  }, [filteredPillars, selectedKey]);

  const isLoading =
    pillarsLoading ||
    pillarsFetching ||
    aiThemesLoading ||
    aiThemesFetching ||
    aiSentimentLoading ||
    aiSentimentFetching;

  const hasPillarsFetchStarted = !!assessmentPeriodId && !!shouldFetchData && !!queryTeamId;

  if (isLoading || (!pillarsData && hasPillarsFetchStarted && (pillarsLoading || pillarsFetching))) {
    return <LoadingSpinner />;
  }

  if (!pillarsData && hasPillarsFetchStarted) {
    return (
      <div className="p-8 text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-3">Team Feedback Themes Not Available</h3>
        {isTeamLeader ? (
          <>
            <p className="text-gray-600 mb-2">
              You are a team leader, but there is no team data available for this assessment period.
            </p>
            <p className="text-gray-600 mb-2">This could mean:</p>
            <ul className="text-gray-600 text-left inline-block mb-3">
              <li>• You haven't been assigned to a team for this assessment period</li>
              <li>• Your team hasn't completed any surveys yet</li>
              <li>• The assessment period hasn't started</li>
            </ul>
            <p className="text-gray-600">
              Please check back later or contact your administrator to assign you to a team.
            </p>
          </>
        ) : (
          <>
            <p className="text-gray-600 mb-2">
              You are not a team leader for this assessment period.
            </p>
            <p className="text-gray-600">
              Switch to <strong>Organization</strong> view to see organization-level analytics.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <PageContent>
      <PageSection padleft padright>

        <PillarsLayout>
          <PillarColumn>
          <Title padbottom='0px' padtop='15px'>Team Feedback</Title>
          <Caption>Select from the theme areas below to review team member feedback</Caption>
            {pillarsLoading ? (
              <div className="text-center py-8 text-gray-500" data-testid="pillars-loading">
                <LoadingSpinner />
              </div>
            ) : pillars.length === 0 ? (
              <div className="text-center py-8 text-gray-500">No data available for this assessment period.</div>
            ) : filteredPillars.length > 0 ? (
              filteredPillars.map((p) => (
                <div key={p.key} className="animate-fade-in w-full">
                  <PillarCard item={p} onOpen={(k: string) => setSelectedKey(k)} showQuestion selected={selectedKey === p.key} />
                </div>
              ))
            ) : (
              <div className="text-center py-12 text-gray-600 text-lg">
                {selectedKey ? 'No feedback themes match your selection.' : 'No feedback themes available yet.'}
              </div>
            )}
          </PillarColumn>

          <PillarDetailWrapper>
            <PillarDetailPane
              selectedKey={selectedKey}
              role="manager"
              pillars={pillars}
            />
          </PillarDetailWrapper>
        </PillarsLayout>
      </PageSection>
    </PageContent>
  );
};


